import { h, Component } from 'preact';
import Store from 'store/store';
import Filter from 'bad-words';
import { getMainPage } from 'utils/bolt';
import { commaSeparatedList, joinClasses } from 'utils/utils';
import parseQueryString from 'utils/parseQueryString';

import Transition from 'components/core/transition/transition';
import Intro from 'components/bolt/quizPhotobooth/intro';
import Quiz from 'components/bolt/quizPhotobooth/quiz';
import Photobooth from 'components/bolt/quizPhotobooth/photobooth';
import Shareable from 'components/bolt/quizPhotobooth/shareable';
import s from 'components/bolt/quizPhotobooth/quizPhotobooth.sss';

import 'store/bolt/quizPhotobooth';

const durationPerTransition = {
	fade: 500,
	'slide-vertical': 2000,
};

export default class QuizPhotobooth extends Component {
	constructor(props) {
		super(props);
		this.advanceStep = this.advanceStep.bind(this);
		this.backStep = this.backStep.bind(this);
		this.backToIntro = this.backToIntro.bind(this);
		this.setResult = this.setResult.bind(this);
		this.setInput = this.setInput.bind(this);
		this.endRevealAnim = this.endRevealAnim.bind(this);
		this.handleLogoClick = this.handleLogoClick.bind(this);
		this.transition = props?.data?.transition || 'slide-vertical';
		this.transitionDuration = durationPerTransition[this.transition] || 1000;

		let sequence = commaSeparatedList(props?.data?.sequence);
		let step = sequence[0];
		let inputs = {};
		let quizResult = null;
		// Easier testing
		if (__IS_LOCAL__ || __IS_DEMO__) {
			let testStep = (parseQueryString().step) || props?.data?.testStep;
			if (testStep) {
				step = testStep;
				if (['browserErrorScreen', 'cameraDeniedScreen', 'deviceErrorScreen'].includes(step)) {
					step = 'photobooth';
				}
				if (!sequence.includes(step)) console.warn(`test step ${step} is not part of the expected steps (${sequence.join(', ')})`);
				if (sequence.indexOf(step) > sequence.indexOf('intro')) {
					inputs = { name: 'Name' };
				}
				if (sequence.indexOf(step) > sequence.indexOf('quiz')) {
					const options = Object.keys(props?.data?.results || {});
					quizResult = options[Math.floor(Math.random() * options.length)];
				}
			}
		}

		this.state = {
			sequence,
			inputs,
			step,
			stepDirection: 'forward',
			revealAnimEnded: false,
			quizResult,
		};
	}

	componentDidMount() {
		this.badWords = new Filter();
		this.badWords.addWords(...commaSeparatedList(this.props?.data?.intro?.profanities));
		const sequence = this.props?.data?.sequence;
		const showResultScreen = this.props?.data?.showResultScreen;
		Store.get().quizPhotobooth.set({ sequence: sequence, step: this.state.step, question: 0, showResultScreen: showResultScreen });
		Store.on('QUIZPHOTOBOOTH_BACK', this.backStep);
		Store.on('QUIZPHOTOBOOTH_BACK_TO_INTRO', this.backToIntro);
		window.addEventListener('resize', this.onWindowResize);
	}

	componentWillUnmount() {
		Store.off('QUIZPHOTOBOOTH_BACK', this.backStep);
		Store.off('QUIZPHOTOBOOTH_BACK_TO_INTRO', this.backToIntro);
		clearTimeout(this.nextQuestionTimer);
		// lottie.destroy();
		window.removeEventListener('resize', this.onWindowResize);
	}

	onWindowResize() {
		// TODO: only call .resize if it is needed (probably call it on the actual animation instances)
		// lottie.resize();
	}

	advanceStep(additionalData) {
		const sequence = this.state.sequence;
		const currentStep = this.state.step;
		const nextStep = sequence.indexOf(currentStep) + 1;
		if (nextStep > sequence.length) return;
		let trackViewLabel = sequence[nextStep];
		const allowChoosingShareableFrame = this.props.data?.shareable?.allowChoosingFrame;
		if (sequence[nextStep] === 'shareable' && allowChoosingShareableFrame) {
			trackViewLabel = 'choose-shareable';
		}
		Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', trackViewLabel);
		this.setState({
			step: sequence[nextStep],
			stepDirection: 'forward',
			// add any additional required data to this.state from the data variable
			...additionalData
		});
		Store.get().quizPhotobooth.set({ step: sequence[nextStep] });
	}

	backStep(clearedData) {
		const sequence = this.state.sequence;
		const currentStep = this.state.step;
		const previousStep = sequence.indexOf(currentStep) - 1;
		if (previousStep < 0) return;
		if (sequence[previousStep] === 'intro') {
			Store.emit('QUIZPHOTOBOOTH_SHAREABLE_RESET');
		}
		if (sequence[previousStep] === 'quiz' ) {
			this.setResult(null);
		}
		Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', sequence[previousStep]);
		this.setState({
			step: sequence[previousStep],
			stepDirection: 'back',
			...clearedData
		});
		Store.get().quizPhotobooth.set({ step: sequence[previousStep] });
	}

	backToIntro() {
		// The steps will reset on their own as they will be remounted
		// Store.emit('QUIZPHOTOBOOTH_INTRO_RESET');
		// Store.emit('QUIZPHOTOBOOTH_QUIZ_RESET');
		// Store.emit('QUIZPHOTOBOOTH_PHOTOBOOTH_RESET');
		// Store.emit('QUIZPHOTOBOOTH_SHAREABLE_RESET');
		this.setState({
			step: 'intro',
			stepDirection: 'back',
			quizResult: null,
		});
		Store.get().quizPhotobooth.set({ step: 'intro' });
		Store.emit('TRACK_QUIZPHOTOBOOTH_REDO_CLICK');
	}

	setResult(result) {
		this.setState({ quizResult: result });
	}

	setInput(input) {
		this.setState(state => ({ inputs: { ...state.inputs, ...input } }));
	}

	endRevealAnim() {
		this.setState({ revealAnimEnded: true });
	}

	handleLogoClick() {
		Store.emit('TRACK_QUIZPHOTOBOOTH_LOGO_CLICK');
		Store.emit('SET_ACTIVE_PAGE', getMainPage());
	}

	renderIntro() {
		const props = this.props;
		return (
			<Intro
				id="quizPhotobooth-intro"
				intro={props.data?.intro}
				assets={props.data?.assets}
				appData={props.appData}
				advanceStep={this.advanceStep}
				setInput={this.setInput}
				isDesktopWidth={props.client?.isDesktopWidth}
				badWords={this.badWords?.list}
				inputValues={this.state.inputs}
				transition={this.transition}
			/>
		);
	}

	renderQuiz() {
		const props = this.props;
		return (
			<Quiz
				id="quizPhotobooth-quiz"
				quiz={props.data?.quiz}
				results={props.data?.results}
				assets={props.data?.assets}
				appData={props.appData}
				transition={this.transition}
				advanceStep={this.advanceStep}
				backStep={this.backStep}
				backToIntro={this.backToIntro}
				endRevealAnim={this.endRevealAnim}
				setResult={this.setResult}
				quizResult={this.state.quizResult}
				inputs={this.state.inputs}
				isDesktopWidth={props?.client?.isDesktopWidth}
			/>
		);
	}

	renderPhotobooth() {
		const props = this.props;
		const client = props.client || {};
		return (
			<Photobooth
				id="quizPhotobooth-photobooth"
				photobooth={props.data?.photobooth}
				assets={props.data?.assets}
				appData={props.appData}
				advanceStep={this.advanceStep}
				backStep={this.backStep}
				backToIntro={this.backToIntro}
				revealAnimEnded={this.state.revealAnimEnded}
				quizResult={this.state.quizResult}
				resultData={props.data?.results || {}}
				isDesktopDevice={client.device === 'desktop'}
				deviceWidthSize={client.deviceWidthSize}
				isDesktopWidth={client.isDesktopWidth}
				isIOS={client.isIOS}
				allowedBrowsers={props.data?.allowedBrowsers}
				noQuiz={!this.state.sequence.includes('quiz')}
				transition={this.transition}
				inputs={this.state.inputs}
				// isEnded={state.step === 'shareable'}
				isActive
				testStep={props.data?.testStep}
			/>
		);
	}

	renderShareable() {
		const props = this.props;
		const client = props.client || {};
		return (
			<Shareable
				id="quizPhotobooth-shareable"
				shareable={props.data?.shareable}
				assets={props.data?.assets}
				appData={props.appData}
				backStep={this.backStep}
				backToIntro={this.backToIntro}
				hasQuiz={this.state.sequence?.includes('quiz')}
				isDesktopDevice={client.device === 'desktop'}
				isDesktopWidth={client.isDesktopWidth}
				client={client}
				isIOS={client.isIOS}
				inputs={this.state.inputs}
				quizResult={this.state.quizResult}
				resultData={props.data?.results || {}}
				showFinalShare
				transitionStyle={this.transition}
				creativeSpace={this.props.creativeSpace}
			/>
		);
	}

	renderContent() {
		return {
			intro: this.renderIntro,
			quiz: this.renderQuiz,
			photobooth: this.renderPhotobooth,
			shareable: this.renderShareable,
		}[this.state.step]?.call(this);
	}

	render(props, state) {
		const assets = props?.data?.assets;
		const isDesktopWidth = props?.client?.isDesktopWidth;
		const showTopImage = !!assets.topImage && (state.step !== 'intro' || assets.showIntroTTImage);

		const backgroundImage = isDesktopWidth ? assets.backgroundDesktop : assets.backgroundMobile;
		const isVideo = backgroundImage?.endsWith('.mp4') || backgroundImage?.endsWith('.webm');
		const containerStyle = [
			`--transition-duration: ${this.transitionDuration}ms`,
			backgroundImage && `background-image: url(${backgroundImage})`
		].filter(e => e).join(';');

		return (
			<div class={joinClasses(s.container, this.props.creativeSpace && s.creativeSpace)} style={containerStyle}>
				{!!backgroundImage && isVideo && <video ref={($ref) => this.$bgVideo = $ref} class={s.video} src={backgroundImage} autoplay loop muted playsinline />}
				{showTopImage && <img src={assets.topImage} class={s.ttHeader} onClick={this.handleLogoClick} alt={assets.topImageAltText && assets.topImageAltText} />}
				<Transition
					duration={this.transitionDuration}
					class={joinClasses(
						s.transitionWrapper, 
						s[`transition-${this.transition}`], 
						s[`transition-direction-${state.stepDirection}`],
						this.props.creativeSpace && s.creativeSpace
					)}
					baseClass={s.transitionElement}
					enteringClass={s.transitionEntering}
					visibleClass={s.transitionVisible}
					leavingClass={s.transitionLeaving}
					emptyClass={s.transitionEmpty}
					noInitialTransition
				>
					{this.renderContent()}
				</Transition>
			</div>
		);
	}
}